import type { ICurrency, ILocale } from 'oscar-typedef'
import { defineStore } from 'pinia'
import { computed, ref, type Ref } from 'vue'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import { useUserStore } from '@/stores'
import { getExecuteOnce } from '@/utils'

export const useInfoAPIStore = defineStore('infoAPI', () => {
  const userStore = useUserStore()
  const currencyList: Ref<ICurrency[]> = ref([])
  const localeList: Ref<ILocale[]> = ref([])

  const currentCurrency = computed(() => {
    const currencyId = userStore.userInfo?.currencyId ?? 3
    if (currencyList.value && currencyList.value.length > 0) {
      return (
        currencyList.value?.find((currency) => currency.id === currencyId)
          ?.name ?? 'USD'
      )
    }
    return 'USD'
  })

  const fetchCurrencyList = getExecuteOnce(
    async ({ force = false }: { force?: boolean }) => {
      if (force || !currencyList.value || currencyList.value.length === 0) {
        const res = await WepinBackendAPI.getInstance().info.getCurrencyList({})
        currencyList.value = res.currency
      }
    },
  )

  const fetchLocaleList = getExecuteOnce(
    async ({ force = false }: { force?: boolean }) => {
      if (force || !localeList.value || localeList.value.length === 0) {
        try {
          const res = await WepinBackendAPI.getInstance().info.getLocaleList({})
          localeList.value = res.locale
        } catch (error) {
          // Locale list fetch error ignored
        }
      }
    },
  )

  const fetchAllInfo = async () => {
    return Promise.all([
      fetchCurrencyList({ force: true }),

      await fetchLocaleList({ force: true }),
    ])
  }

  const reset = () => {
    currencyList.value = []
    localeList.value = []
  }

  return {
    currencyList,
    localeList,
    currentCurrency,
    fetchCurrencyList,
    fetchLocaleList,
    fetchAllInfo,
    reset,
  }
})
